import { useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  UserPlus,
  KeyRound,
  Loader2,
  Copy,
  Check,
  Shield,
  User,
  Mail,
  X,
  Pencil,
  Phone,
  CalendarDays,
  DollarSign,
  BadgeCheck,
  Plus,
  Trash2,
  AlertTriangle,
  ShieldCheck,
} from "lucide-react";
import ComplianceEditor from "@/components/ComplianceEditor";
import { StatusBadge as StatusBadgePill } from "@/components/ComplianceEditor";
import AvailabilityEditor from "@/components/AvailabilityEditor";
import TagInput from "@/components/TagInput";
import PasswordHint from "@/components/admin-team/PasswordHint";
import CertificationsEditor from "@/components/admin-team/CertificationsEditor";
import InviteCaregiverModal from "@/components/admin-team/InviteCaregiverModal";
import UserModal from "@/components/admin-team/UserModal";

const blankNew = {
  email: "",
  name: "",
  password: "",
  role: "caregiver",
  phone: "",
  address: "",
  dob: "",
  hire_date: "",
  hourly_rate: "",
  certifications: [],
  compliance_items: [],
  emergency_contact_name: "",
  emergency_contact_phone: "",
  skills: [],
  languages: [],
  notes: "",
  worker_id: "",
};

export default function AdminTeam() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter =
    location.pathname.endsWith("/team/caregivers")
      ? "caregiver"
      : location.pathname.endsWith("/team/admins")
      ? "admin"
      : "all";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [invites, setInvites] = useState([]);
  const [newUser, setNewUser] = useState(blankNew);
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState(null); // user object
  const [editForm, setEditForm] = useState(null);
  const [editingSaving, setEditingSaving] = useState(false);

  const [expiring, setExpiring] = useState([]);
  const [compliance, setCompliance] = useState(null);

  const [resetLink, setResetLink] = useState(null);
  const [busyUser, setBusyUser] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, exp, comp, inv] = await Promise.all([
        api.get("/auth/users"),
        api.get("/admin/expiring-certifications", { params: { days: 30 } }),
        api.get("/admin/compliance-summary"),
        api.get("/admin/invites").catch(() => ({ data: [] })),
      ]);
      setUsers(u.data);
      setExpiring(exp.data.items || []);
      setCompliance(comp.data);
      setInvites(inv.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Filter users by role based on URL (/team/caregivers, /team/admins, or /team)
  const filteredUsers = useMemo(() => {
    if (roleFilter === "all") return users;
    return users.filter((u) => u.role === roleFilter);
  }, [users, roleFilter]);

  // Deep-link: ?user=<id> opens the edit modal for that user once data loads
  useEffect(() => {
    const targetId = searchParams.get("user");
    if (!targetId || loading || editing) return;
    const target = users.find((u) => u.id === targetId);
    if (target) {
      openEdit(target);
      // Strip the query so refresh/back doesn't re-open
      searchParams.delete("user");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, loading, searchParams]);

  const addUser = async () => {
    if (!newUser.email || !newUser.name) {
      toast.error("Email and name are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...newUser,
        hourly_rate:
          newUser.hourly_rate === "" ? null : Number(newUser.hourly_rate),
        certifications: (newUser.certifications || []).filter(
          (c) => c.name || c.number
        ),
        compliance_items: (newUser.compliance_items || []).filter(
          (c) => c.type && c.status
        ),
      };
      await api.post("/auth/register", payload);
      toast.success("User created");
      setShowAdd(false);
      setNewUser(blankNew);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not create user");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({
      name: u.name || "",
      email: u.email || "",
      role: u.role || "caregiver",
      active: u.active !== false,
      phone: u.phone || "",
      address: u.address || "",
      dob: u.dob || "",
      hire_date: u.hire_date || "",
      hourly_rate: u.hourly_rate == null ? "" : String(u.hourly_rate),
      certifications: u.certifications || [],
      compliance_items: u.compliance_items || [],
      availability: u.availability || { weekly_pattern: {}, time_off: [] },
      emergency_contact_name: u.emergency_contact_name || "",
      emergency_contact_phone: u.emergency_contact_phone || "",
      skills: u.skills || [],
      languages: u.languages || [],
      notes: u.notes || "",
      worker_id: u.worker_id || "",
    });
  };

  const closeEdit = () => {
    if (editingSaving) return;
    setEditing(null);
    setEditForm(null);
  };

  const saveEdit = async () => {
    setEditingSaving(true);
    try {
      const payload = {
        ...editForm,
        hourly_rate:
          editForm.hourly_rate === "" ? null : Number(editForm.hourly_rate),
        certifications: (editForm.certifications || []).filter(
          (c) => c.name || c.number
        ),
        compliance_items: (editForm.compliance_items || []).filter(
          (c) => c.type && c.status
        ),
      };
      await api.put(`/auth/users/${editing.id}`, payload);
      toast.success("User updated");
      closeEdit();
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg : detail;
      toast.error(msg || "Could not save");
    } finally {
      setEditingSaving(false);
    }
  };

  const issueReset = async (u) => {
    setBusyUser(u.id);
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-link`);
      setResetLink({ user: u, ...data });
      setCopied(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate reset link");
    } finally {
      setBusyUser(null);
    }
  };

  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink.reset_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Reset link copied");
    } catch {
      window.prompt("Copy this link:", resetLink.reset_url);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Access</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            {roleFilter === "caregiver"
              ? "Caregivers"
              : roleFilter === "admin"
              ? "Admins"
              : "Team"}
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            {roleFilter === "caregiver"
              ? "Field caregivers — profiles, certifications, compliance, and reset links."
              : roleFilter === "admin"
              ? "Agency admins with full console access."
              : "Manage admin and caregiver profiles, certifications, and access."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowInvite(true)}
            className="btn-secondary"
            data-testid="invite-user-button"
          >
            <Mail className="h-4 w-4" /> Invite caregiver
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="btn-primary"
            data-testid="new-user-button"
          >
            <UserPlus className="h-4 w-4" /> Add user
          </button>
        </div>
      </div>

      {compliance && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="compliance-summary">
          <div className="card-surface p-4 flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="label-eyebrow">Fully verified</div>
              <div className="font-heading text-2xl font-semibold mt-0.5">
                {compliance.fully_verified_caregivers}
                <span className="text-sm font-normal text-clinical-subtext"> / {compliance.total_caregivers}</span>
              </div>
              <div className="text-[11px] text-clinical-subtext mt-0.5">caregivers</div>
            </div>
          </div>
          <div className="card-surface p-4 flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <div className="label-eyebrow">Expiring soon</div>
              <div className="font-heading text-2xl font-semibold mt-0.5">{compliance.counters.expiring_soon || 0}</div>
              <div className="text-[11px] text-clinical-subtext mt-0.5">within 30 days</div>
            </div>
          </div>
          <div className="card-surface p-4 flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <div>
              <div className="label-eyebrow">Expired</div>
              <div className="font-heading text-2xl font-semibold mt-0.5">{compliance.counters.expired || 0}</div>
              <div className="text-[11px] text-clinical-subtext mt-0.5">past expiry</div>
            </div>
          </div>
          <div className="card-surface p-4 flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-clinical-primary/10 text-clinical-primary">
              <BadgeCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="label-eyebrow">In progress</div>
              <div className="font-heading text-2xl font-semibold mt-0.5">
                {(compliance.counters.pending || 0) + (compliance.counters.submitted || 0)}
              </div>
              <div className="text-[11px] text-clinical-subtext mt-0.5">pending or submitted</div>
            </div>
          </div>
        </div>
      )}

      {compliance && compliance.flagged.length > 0 && (
        <div
          className="card-surface p-4 border-amber-200 bg-amber-50/40"
          data-testid="compliance-flagged-widget"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h2 className="font-heading text-sm font-medium">
              Compliance needs attention
            </h2>
            <span className="ml-auto label-eyebrow">{compliance.flagged.length}</span>
          </div>
          <ul className="space-y-1.5 text-sm" data-testid="compliance-flagged-list">
            {compliance.flagged.slice(0, 10).map((f) => (
              <li
                key={f.item_id}
                className="flex items-center gap-2"
                data-testid={`flagged-${f.item_id}`}
              >
                <ShieldCheck className="h-3.5 w-3.5 text-clinical-subtext" />
                <span className="font-medium text-clinical-text">{f.name}</span>
                <span className="text-clinical-subtext">·</span>
                <span className="text-clinical-subtext truncate">{f.label}</span>
                {f.expires_on && (
                  <span className="text-clinical-subtext text-[11px]">
                    · {f.status === "expired" ? "expired" : "expires"} {new Date(f.expires_on + "T00:00:00").toLocaleDateString()}
                  </span>
                )}
                <span className="ml-auto">
                  <StatusBadgePill status={f.status} daysLeft={f.days_left} />
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {expiring.length > 0 && (
        <div
          className="card-surface p-4 border-amber-200 bg-amber-50/60"
          data-testid="expiring-certs-widget"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <h2 className="font-heading text-sm font-medium">
              Certifications expiring soon
            </h2>
            <span className="ml-auto label-eyebrow">
              {expiring.reduce((sum, e) => sum + e.expiring.length, 0)}
            </span>
          </div>
          <ul className="space-y-1.5 text-sm">
            {expiring.map((e) =>
              e.expiring.map((c, i) => (
                <li
                  key={`${e.user_id}-${i}`}
                  className="flex items-center gap-2 text-amber-900"
                  data-testid={`expiring-${e.user_id}-${i}`}
                >
                  <BadgeCheck className="h-3.5 w-3.5" />
                  <span className="font-medium">{e.name}</span>
                  <span className="text-clinical-subtext">·</span>
                  <span>
                    {c.name} {c.number ? `(${c.number})` : ""} expires{" "}
                    {new Date(c.expires_on + "T00:00:00").toLocaleDateString()}
                  </span>
                  <span
                    className={`ml-auto pill ${
                      c.days_left < 0
                        ? "!bg-destructive/10 !text-destructive"
                        : "!bg-amber-200 !text-amber-900"
                    }`}
                  >
                    {c.days_left < 0
                      ? `Expired ${Math.abs(c.days_left)}d ago`
                      : `${c.days_left}d left`}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <div
          className="card-surface divide-y divide-clinical-border"
          data-testid="users-list"
        >
          {filteredUsers.length === 0 ? (
            <div className="py-12 text-center text-sm text-clinical-subtext" data-testid="users-empty">
              No {roleFilter === "all" ? "users" : roleFilter + "s"} yet.
            </div>
          ) : (
            filteredUsers.map((u) => {
            const inactive = u.active === false;
            return (
              <div
                key={u.id}
                className={`flex flex-wrap items-center gap-3 p-4 ${
                  inactive ? "opacity-60" : ""
                }`}
                data-testid={`user-row-${u.id}`}
              >
                <span
                  className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                    u.role === "admin"
                      ? "bg-clinical-primary/10 text-clinical-primary"
                      : "bg-afyanex-cyan/15 text-clinical-primary"
                  }`}
                >
                  {u.role === "admin" ? (
                    <Shield className="h-4 w-4" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-clinical-text truncate">
                    {u.name}
                    {inactive && (
                      <span className="ml-2 pill bg-clinical-bg text-clinical-subtext">
                        inactive
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-clinical-subtext flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {u.email}
                    </span>
                    {u.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {u.phone}
                      </span>
                    )}
                    {u.hire_date && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        hired {u.hire_date}
                      </span>
                    )}
                    {u.hourly_rate != null && (
                      <span className="inline-flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {Number(u.hourly_rate).toFixed(2)}/h
                      </span>
                    )}
                    <span className="pill bg-clinical-bg text-clinical-subtext capitalize">
                      {u.role}
                    </span>
                    {(u.certifications || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-clinical-primary">
                        <BadgeCheck className="h-3 w-3" />
                        {u.certifications.length} cert
                        {u.certifications.length === 1 ? "" : "s"}
                      </span>
                    )}
                    {(u.compliance_items || []).length > 0 && (() => {
                      const verified = (u.compliance_items || []).filter(
                        (c) => c.status === "verified" && (!c.expires_on || new Date(c.expires_on + "T00:00:00") > new Date())
                      ).length;
                      const total = u.compliance_items.length;
                      const needsAttention = (u.compliance_items || []).some((c) => {
                        if (c.status === "rejected") return true;
                        if (c.status === "verified" && c.expires_on) {
                          const d = Math.ceil((new Date(c.expires_on + "T00:00:00").getTime() - Date.now()) / 86400000);
                          return d <= 30;
                        }
                        return false;
                      });
                      return (
                        <span
                          className={`inline-flex items-center gap-1 ${needsAttention ? "text-amber-700" : "text-emerald-700"}`}
                          data-testid={`compliance-chip-${u.id}`}
                        >
                          <ShieldCheck className="h-3 w-3" />
                          {verified}/{total} verified
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => openEdit(u)}
                  className="btn-secondary"
                  data-testid={`edit-user-${u.id}`}
                >
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                <button
                  onClick={() => issueReset(u)}
                  disabled={busyUser === u.id}
                  className="btn-ghost"
                  data-testid={`reset-link-${u.id}`}
                  title="Issue password reset link"
                >
                  {busyUser === u.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4" />
                  )}
                </button>
              </div>
            );
          })
          )}
        </div>
      )}

      {/* Add user modal */}
      {showAdd && (
        <UserModal
          title="Add user"
          form={newUser}
          setForm={setNewUser}
          onClose={() => setShowAdd(false)}
          onSave={addUser}
          saving={saving}
          showPassword
        />
      )}

      {/* Edit user modal */}
      {editing && editForm && (
        <UserModal
          title={`Edit · ${editing.name}`}
          form={editForm}
          setForm={setEditForm}
          onClose={closeEdit}
          onSave={saveEdit}
          saving={editingSaving}
          showActive
          isEdit
        />
      )}

      {/* Reset link modal */}
      {resetLink && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setResetLink(null)}
        >
          <div
            className="bg-white rounded-2xl border border-clinical-border w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="reset-link-modal"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border">
              <h3 className="font-heading text-lg font-medium">
                Password reset link
              </h3>
              <button
                onClick={() => setResetLink(null)}
                className="btn-ghost p-1.5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-clinical-subtext">
                One-time link for{" "}
                <span className="font-medium text-clinical-text">
                  {resetLink.user.name}
                </span>
                . Share via a private channel. It expires in 1 hour and can only
                be used once.
              </p>
              <div className="rounded-lg border border-clinical-border bg-clinical-bg p-3 break-all font-mono text-xs text-clinical-text">
                {resetLink.reset_url}
              </div>
              <button
                onClick={copyResetLink}
                className="btn-primary w-full"
                data-testid="copy-reset-link"
              >
                {copied ? (
                  <>
                    <Check className="h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" /> Copy link
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvite && (
        <InviteCaregiverModal
          invites={invites}
          onClose={() => setShowInvite(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

